import { WSText } from "@wingspanhq/fe-component-library";
import React from "react";
import { Benefit, BenefitCategory, BenefitGate } from "./types";

export const BENEFITS_SECTION_SLUGS = [
  "my-benefits",
  "insurance",
  "axis-insurance"
] as const;

export type BenefitsSectionSlug = (typeof BENEFITS_SECTION_SLUGS)[number];

export type BenefitsTab = {
  slug: string;
  label: string;
  description?: React.ReactNode;
  /** Narrows the section's benefits down to this tab. Omit to show all of them. */
  match?: (benefit: Benefit) => boolean;
};

export type BenefitsSection = {
  slug: BenefitsSectionSlug;
  title: string;
  intro?: React.ReactNode;
  /** Only benefits carrying this gate. Omit for ungated catalog benefits. */
  gate?: BenefitGate;
  /**
   * `true` lists benefits the member has to pay extra for, `false` lists the
   * ones the membership already covers.
   */
  isAdditional: boolean;
  categories?: BenefitCategory[];
  /** Offers the "Request a new benefit" typeform. */
  canRequestBenefit?: boolean;
  tabs: BenefitsTab[];
};

export const BENEFITS_SECTIONS: BenefitsSection[] = [
  {
    slug: "my-benefits",
    title: "Premium perks",
    isAdditional: false,
    tabs: [
      {
        slug: "legal-and-financial-support",
        label: "Legal and financial support",
        description: (
          <WSText my="2XL">
            Through Wingspan, you can get help with basic legal matters and work
            with a financial wellness coach to guide you through life’s toughest
            logistics.
          </WSText>
        ),
        match: benefit =>
          [BenefitCategory.Finances, BenefitCategory.Legal].includes(
            benefit.category
          )
      },
      {
        slug: "mind-and-body-support",
        label: "Mind and body support",
        description: (
          <WSText my="2XL">
            Get discounts on alternative medicine and chiropractic through
            Wingspan.{" "}
            <b>Keep an eye out for Therapy Benefits as well, coming soon!</b>
          </WSText>
        ),
        match: benefit =>
          [BenefitCategory.Wellness, BenefitCategory.Other].includes(
            benefit.category
          )
      },
      {
        slug: "save-money-on-medical-bills",
        label: "Save money on medical bills",
        description: (
          <WSText my="2XL">
            Skip the doctor and get free 24/7 access to virtual care, 1:1
            support negotiating insurance bills, savings on prescriptions, and
            more included in your Wingspan Membership.
          </WSText>
        ),
        match: benefit => benefit.category === BenefitCategory.Health
      }
    ]
  },
  {
    slug: "insurance",
    title: "Insurance",
    intro: (
      <WSText.ParagraphSm color="gray600">
        Accessing high quality insurance for independent contractors is
        difficult. Wingspan has partnered with providers who have designed plans
        specifically for independent professionals.
      </WSText.ParagraphSm>
    ),
    isAdditional: true,
    categories: [BenefitCategory.Insurance],
    canRequestBenefit: true,
    tabs: [
      { slug: "all", label: "All" },
      {
        slug: "independent-contractor",
        label: "Independent contractor insurance",
        match: benefit => !!benefit.isAssurely
      },
      {
        slug: "personal",
        label: "Personal insurance",
        match: benefit => !benefit.isAssurely
      }
    ]
  },
  {
    slug: "axis-insurance",
    title: "Insurance",
    intro: (
      <WSText.ParagraphSm color="gray600">
        Accessing high quality insurance for independent contractors is
        difficult. Wingspan has partnered with providers who have designed plans
        specifically for independent professionals.
      </WSText.ParagraphSm>
    ),
    gate: BenefitGate.AxisPartner,
    isAdditional: true,
    tabs: [{ slug: "all", label: "All" }]
  }
];

export const BENEFITS_SECTIONS_BY_SLUG: Record<
  BenefitsSectionSlug,
  BenefitsSection
> = BENEFITS_SECTIONS.reduce(
  (acc, section) => ({ ...acc, [section.slug]: section }),
  {} as Record<BenefitsSectionSlug, BenefitsSection>
);
